import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Card,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import apiRequest from "../../../apiRequest";
import { endPoints } from "../../../apis";
import { v4 as uuidv4 } from "uuid";
import { useParams, useNavigate } from "react-router-dom";

function CreateForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [newData, setNewData] = useState({ name: null });

  const [sections, setSections] = useState([
    {
      id: uuidv4(),
      title_en: "",
      title_fr: "",
      fields: [
        {
          id: uuidv4(),
          label_en: "",
          label_fr: "",
          name: "",
          type: "text",
          hint: "",
          required: false,
          options: [],
        },
      ],
    },
  ]);

  const fetchFormData = useCallback(
    async (formId) => {
      setLoading(true);
      try {
        const response = await apiRequest.get(`${endPoints.GET_SINGLE}/${formId}`, {
          headers: { "x-access-token": accessToken },
        });
        if (response.success) {
          const { name, form_data } = response.data;
          setNewData({ name });
          setSections(JSON.parse(form_data));
        }
      } catch (err) {
        console.error("Error fetching form data:", err);
        setError("Failed to fetch form data.");
      } finally {
        setLoading(false);
      }
    },
    [accessToken]
  );

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    setAccessToken(token);
    if (id && token) {
      setIsEditMode(true);
      fetchFormData(id);
    }
  }, [id, fetchFormData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data = { name: newData.name, form_data: JSON.stringify(sections) };
      const endpoint = isEditMode ? `${endPoints.UPDATE_FORM}/${id}` : endPoints.ADD_FORM;
      const method = isEditMode ? "put" : "post";
      const response = await apiRequest[method](endpoint, data, {
        headers: { "x-access-token": accessToken },
      });

      if (response.success) {
        setSuccess(isEditMode ? "Form updated successfully." : "Form added successfully.");
        handleBack();
      } else {
        setError(response.message || "Failed to save form.");
      }
    } catch (err) {
      console.error("Error submitting form:", err);
      setError("An error occurred while processing your request.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setNewData({ name: e.target.value });
  };

  const handleSectionTitleChange = (id, value, lang) => {
    setSections(
      sections.map((section) =>
        section.id === id ? { ...section, [`title_${lang}`]: value } : section
      )
    );
  };

  const handleFieldChange = (sectionId, fieldId, field, value) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: section.fields.map((f) => {
                if (f.id === fieldId) {
                  let updated = { ...f, [field]: value };
                  if (field === "type" && value !== "select") updated.options = [];
                  return updated;
                }
                return f;
              }),
            }
          : section
      )
    );
  };

  const handleAddField = (sectionId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              fields: [
                ...section.fields,
                {
                  id: uuidv4(),
                  label_en: "",
                  label_fr: "",
                  name: "",
                  type: "text",
                  hint: "",
                  required: false,
                  options: [],
                },
              ],
            }
          : section
      )
    );
  };

  const handleRemoveField = (sectionId, fieldId) => {
    setSections(
      sections.map((section) =>
        section.id === sectionId
          ? { ...section, fields: section.fields.filter((f) => f.id !== fieldId) }
          : section
      )
    );
  };

  const handleAddSection = () => {
    setSections([
      ...sections,
      {
        id: uuidv4(),
        title_en: "",
        title_fr: "",
        fields: [
          {
            id: uuidv4(),
            label_en: "",
            label_fr: "",
            name: "",
            type: "text",
            hint: "",
            required: false,
            options: [],
          },
        ],
      },
    ]);
  };

  const handleRemoveSection = (sectionId) => {
    setSections(sections.filter((section) => section.id !== sectionId));
  };

  const handleBack = () => navigate("/dashboard/settings/custom-form");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Custom Form Builder
                </MDTypography>
              </MDBox>
              <MDBox pt={3} px={3}>
                <form onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    label="Form Name"
                    value={newData.name || ""}
                    onChange={handleInputChange}
                    margin="normal"
                    required
                    variant="outlined"
                    InputLabelProps={{ shrink: !!newData.name }}
                  />

                  {sections.map((section) => (
                    <MDBox key={section.id} my={2} p={2} border="1px solid #ddd" borderRadius="8px">
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Section Name EN"
                            value={section.title_en}
                            onChange={(e) => handleSectionTitleChange(section.id, e.target.value, "en")}
                            required
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            label="Section Name FR"
                            value={section.title_fr}
                            onChange={(e) => handleSectionTitleChange(section.id, e.target.value, "fr")}
                            required
                          />
                        </Grid>
                      </Grid>

                      {section.fields.map((field) => (
                        <Grid container spacing={2} key={field.id} mt={2}>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              label="Label EN"
                              value={field.label_en}
                              onChange={(e) => handleFieldChange(section.id, field.id, "label_en", e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              label="Label FR"
                              value={field.label_fr}
                              onChange={(e) => handleFieldChange(section.id, field.id, "label_fr", e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              label="Key"
                              value={field.name}
                              onChange={(e) => handleFieldChange(section.id, field.id, "name", e.target.value)}
                              required
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <FormControl fullWidth>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={field.type}
                                onChange={(e) => handleFieldChange(section.id, field.id, "type", e.target.value)}
                                style={{ padding: "10px", borderRadius: "4px" }}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="image">Image</MenuItem>
                                <MenuItem value="date">Date</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              label="Hint"
                              value={field.hint}
                              onChange={(e) => handleFieldChange(section.id, field.id, "hint", e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.required}
                                  onChange={(e) =>
                                    handleFieldChange(section.id, field.id, "required", e.target.checked)
                                  }
                                />
                              }
                              label="Required"
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <Button
                              color="error"
                              variant="contained"
                              onClick={() => handleRemoveField(section.id, field.id)}
                            >
                              Remove Field
                            </Button>
                          </Grid>

                          {field.type === "select" && (
                            <Grid item xs={12}>
                              <MDTypography variant="subtitle2">Dropdown Options</MDTypography>
                              <Grid container spacing={2} sx={{ mt: 1, flexWrap: "wrap" }}>
                                {field.options?.map((option, index) => (
                                  <Grid item xs={12} sm={4} md={3} key={index}>
                                    <TextField
                                      fullWidth
                                      label={`Option ${index + 1}`}
                                      value={option}
                                      onChange={(e) => {
                                        const updatedOptions = [...field.options];
                                        updatedOptions[index] = e.target.value;
                                        handleFieldChange(section.id, field.id, "options", updatedOptions);
                                      }}
                                      InputProps={{
                                        endAdornment: (
                                          <InputAdornment position="end">
                                            <IconButton
                                              onClick={() => {
                                                const updatedOptions = field.options.filter((_, i) => i !== index);
                                                handleFieldChange(section.id, field.id, "options", updatedOptions);
                                              }}
                                              color="error"
                                              size="small"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  </Grid>
                                ))}
                              </Grid>

                              <Button
                                onClick={() => {
                                  const opts = [...(field.options || []), ""];
                                  handleFieldChange(section.id, field.id, "options", opts);
                                }}
                                variant="contained"
                                color="error"
                                sx={{ mt: 2 }}
                              >
                                Add Option
                              </Button>
                            </Grid>
                          )}
                        </Grid>
                      ))}

                      <Button
                        variant="contained"
                        onClick={() => handleAddField(section.id)}
                        sx={{ mt: 2, mr: 2 }}
                        style={{ color : "white" }}
                      >
                        Add Field
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleRemoveSection(section.id)}
                        sx={{ mt: 2 }}
                      >
                        Remove Section
                      </Button>
                    </MDBox>
                  ))}

                  <Button variant="contained" onClick={handleAddSection} sx={{ mt: 3 }} style={{ color : "white" }}>
                    Add Section
                  </Button>

                  <Button type="submit" variant="contained" color="primary" sx={{ mt: 3, mb: 2, float: "right" }} style={{ color : "white" }}>
                    Save Form
                  </Button>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={!!error || !!success}
        autoHideDuration={3000}
        onClose={() => {
          setError(null);
          setSuccess(null);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => {
            setError(null);
            setSuccess(null);
          }}
          severity={error ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {error || success}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default CreateForm;
